/** Dialogs for the Exceptions & Escalations Centre: act (assign/escalate/resolve/dismiss/note), detail sheet, thresholds. */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Loader2, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { SEV, STATUS_CLS, CAT_LABEL, inr, fmtDate, fmtWhen } from "./exceptionUtils";

const META = {
  assign: { title: "Assign owner", verb: "Assign", note: "Message to the new owner (optional)", needNote: false },
  escalate: { title: "Escalate", verb: "Escalate", note: "Why is this being escalated? (required — goes to the audit trail)", needNote: true },
  resolve: { title: "Resolve exception", verb: "Mark resolved", note: "What fixed it? (required)", needNote: true },
  dismiss: { title: "Dismiss — not an issue", verb: "Dismiss", note: "Why is this not an issue? (required)", needNote: true },
  note: { title: "Add note", verb: "Save note", note: "Note", needNote: true },
  reopen: { title: "Reopen exception", verb: "Reopen", note: "Reason (optional)", needNote: false },
};

export function ActDialog({ act, onClose, onDone }) {
  const [note, setNote] = useState("");
  const [owner, setOwner] = useState("");
  const [owners, setOwners] = useState([]);
  const [busy, setBusy] = useState(false);
  const m = META[act?.kind] || META.note;
  useEffect(() => { if (act?.kind === "assign") api.get("/exception-centre/owners").then(({ data }) => setOwners(data.items)).catch(() => {}); }, [act]);
  if (!act) return null;
  const it = act.item;
  const nextLevel = Math.min(3, (it.escalation_level || 0) + 1);
  const submit = async () => {
    if (m.needNote && !note.trim()) return toast.error("A note is required");
    if (act.kind === "assign" && !owner.includes("@")) return toast.error("Pick an owner");
    setBusy(true);
    try {
      const body = { key: it.key, note: note.trim() || null, title: it.title, category: it.category, customer: it.customer, route: it.route };
      const { data } = await api.post(`/exception-centre/${act.kind}`, act.kind === "assign" ? { ...body, owner_email: owner } : body);
      toast.success(act.kind === "escalate" ? `Escalated to ${data.escalation_label} · ${data.notified} notified` : `${m.verb} done`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid={`act-dialog-${act.kind}`}>
        <DialogHeader><DialogTitle className="font-heading">{m.title}</DialogTitle></DialogHeader>
        <div className="rounded-md border border-slate-200 bg-slate-50 p-2.5 text-[12px]">
          <div className="font-semibold text-slate-900">{it.title}</div>
          <div className="text-slate-500">{it.reason}</div>
        </div>
        {act.kind === "escalate" && <div className="text-[12px] text-slate-600">Current: <b>{it.escalation_label}</b> → Next: <b data-testid="escalate-next">{["L0 · Team", "L1 · Team lead", "L2 · Management", "L3 · Director"][nextLevel]}</b>. {nextLevel >= 2 ? "Management will be notified and it lands in their Action Centre." : "The team will be notified."}</div>}
        {act.kind === "assign" && (
          <label className="block space-y-1"><span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Owner</span>
            <select value={owner} onChange={(e) => setOwner(e.target.value)} data-testid="assign-owner" className="h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-[12px]">
              <option value="">Select a person…</option>
              {owners.map((o) => <option key={o.email} value={o.email}>{o.name || o.email} · {o.role}</option>)}
            </select>
          </label>
        )}
        <label className="block space-y-1"><span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{m.note}</span>
          <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} data-testid="act-note" className="text-[12px]" /></label>
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose} data-testid="act-cancel">Cancel</Button>
          <Button size="sm" onClick={submit} disabled={busy} data-testid="act-submit" variant={act.kind === "dismiss" ? "secondary" : "default"}>{busy && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />}{m.verb}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const Row = ({ k, v }) => <div className="flex gap-2 text-[12px]"><span className="w-32 shrink-0 text-slate-500">{k}</span><span className="text-slate-800 break-words">{v ?? "—"}</span></div>;

export function DetailSheet({ item, onClose, onAct }) {
  const [hist, setHist] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { if (item) { setHist(null); api.get("/exception-centre/history", { params: { key: item.key } }).then(({ data }) => setHist(data.items)).catch(() => setHist([])); } }, [item]);
  if (!item) return null;
  const s = SEV[item.severity];
  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="exception-detail">
        <SheetHeader>
          <div className="flex items-center gap-2"><span className={`rounded border px-1.5 py-0.5 font-mono text-[10px] font-bold ${s.cls}`}>{item.severity}</span>
            <span className={`rounded border px-1.5 py-0.5 font-mono text-[10px] ${STATUS_CLS[item.status]}`}>{item.status.replace("_", " ")}</span>
            <span className="text-[11px] text-slate-500">{item.code_label} · {CAT_LABEL[item.category]}</span></div>
          <SheetTitle className="font-heading text-lg leading-tight">{item.title}</SheetTitle>
        </SheetHeader>
        <div className="mt-4 space-y-4">
          <div className="rounded-lg border border-slate-200 p-3 space-y-1.5">
            <Row k="Customer" v={item.customer} /><Row k="Record" v={item.record} /><Row k="Owner" v={item.owner} /><Row k="Age" v={`${item.age_days} days`} />
            <Row k="Due date" v={<span className={item.is_overdue ? "text-rose-600 font-semibold" : ""}>{fmtDate(item.due_at)}{item.is_overdue ? " · overdue" : ""}</span>} />
            <Row k="Reason" v={item.reason} /><Row k="Recommended action" v={<b>{item.recommended_action}</b>} />
            <Row k="Escalation level" v={item.escalation_label} />{item.value_inr != null && <Row k="Value" v={inr(item.value_inr)} />}
          </div>
          <div className="flex flex-wrap gap-1.5">
            <Button size="sm" onClick={() => navigate(item.route)} data-testid="detail-open-record"><ExternalLink className="h-3.5 w-3.5 mr-1" />Open record</Button>
            {item.can_act && <>
              <Button size="sm" variant="outline" onClick={() => onAct("assign", item)} data-testid="detail-assign">Assign</Button>
              {item.status === "OPEN" && <Button size="sm" variant="outline" onClick={() => onAct("start", item)} data-testid="detail-start">Start working</Button>}
              {item.escalation_level < 3 && <Button size="sm" variant="outline" className="border-rose-200 text-rose-700" onClick={() => onAct("escalate", item)} data-testid="detail-escalate">Escalate</Button>}
              <Button size="sm" variant="outline" className="border-emerald-200 text-emerald-700" onClick={() => onAct("resolve", item)} data-testid="detail-resolve">Resolve</Button>
              <Button size="sm" variant="ghost" onClick={() => onAct("dismiss", item)} data-testid="detail-dismiss">Not an issue</Button>
              <Button size="sm" variant="ghost" onClick={() => onAct("note", item)} data-testid="detail-note">Add note</Button>
            </>}
          </div>
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">History</div>
            {!hist && <div className="text-[12px] text-slate-400 flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" />Loading…</div>}
            {hist?.length === 0 && <div className="text-[12px] text-slate-400" data-testid="history-empty">No actions yet — detected automatically by the scanner.</div>}
            <div className="space-y-1.5">
              {hist?.slice().reverse().map((h) => (
                <div key={h.id || h.at} className="rounded-md border border-slate-100 bg-slate-50/60 px-2.5 py-1.5 text-[12px]" data-testid="history-entry">
                  <div className="flex items-center gap-2"><span className="font-mono text-[10px] font-semibold text-slate-700">{h.action.replace(/_/g, " ")}</span><span className="text-slate-400">{h.by}</span><span className="ml-auto text-[10px] text-slate-400">{fmtWhen(h.at)}</span></div>
                  {h.note && <div className="text-slate-600 mt-0.5">{h.note}</div>}
                </div>
              ))}
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export function SettingsDialog({ open, onClose, onSaved }) {
  const [cfg, setCfg] = useState(null);
  const [vals, setVals] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) api.get("/exception-centre/config").then(({ data }) => { setCfg(data); setVals(data.values); }); }, [open]);
  const save = async () => {
    setBusy(true);
    try { await api.put("/exception-centre/config", { values: vals }); toast.success("Thresholds saved — board refreshed"); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="settings-dialog">
        <DialogHeader><DialogTitle className="font-heading">Exception thresholds</DialogTitle></DialogHeader>
        {!cfg ? <div className="py-8 text-center text-sm text-slate-500">Loading…</div> : (
          <>
            <p className="text-[12px] text-slate-500">Severity auto-escalates as records move further past these thresholds (ATTENTION → HIGH at 2× → CRITICAL at 4×).</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {Object.keys(cfg.labels).map((k) => (
                <label key={k} className="block space-y-1"><span className="text-[11px] text-slate-600">{cfg.labels[k]}</span>
                  <Input type="number" className="h-8 text-[12px]" value={vals[k] ?? ""} disabled={!cfg.can_edit} data-testid={`cfg-${k}`}
                    onChange={(e) => setVals({ ...vals, [k]: e.target.value === "" ? cfg.defaults[k] : Number(e.target.value) })} /></label>
              ))}
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setVals(cfg.defaults)} disabled={!cfg.can_edit} data-testid="cfg-reset">Reset to defaults</Button>
              <Button size="sm" onClick={save} disabled={busy || !cfg.can_edit} data-testid="cfg-save">{busy && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />}Save</Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
